import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { cn } from "../lib/utils";
import { formatDateTime, formatInitials } from "@tellescope/utils";
import { Clock, File } from "lucide-react";
import { useState } from "react";

type AttachmentKind = "image" | "video" | "audio" | "file";

/**
 * Classifies an attachment when its kind can be determined confidently from the MIME type,
 * file name, or URL extension. Returns `undefined` for typeless, extensionless URLs (e.g. SMS
 * `mediaURLs` pointing at opaque storage URLs), which must be probed at render time instead.
 */
const getDeclaredKind = (attachment: MessageAttachment): AttachmentKind | undefined => {
  const type = (attachment.type ?? "").toLowerCase();
  const searchString = `${attachment.name ?? ""} ${attachment.url}`.toLowerCase();

  if (type.startsWith("video/") || /\.(mp4|mov|webm|m4v|ogv)(\?|$)/.test(searchString)) {
    return "video";
  }
  if (type.startsWith("audio/") || /\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/.test(searchString)) {
    return "audio";
  }
  if (type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)(\?|$)/.test(searchString)) {
    return "image";
  }
  if (
    type.startsWith("application/") ||
    type.startsWith("text/") ||
    /\.(pdf|docx?|xlsx?|pptx?|csv|txt|zip|rar|7z)(\?|$)/.test(searchString)
  ) {
    return "file";
  }
  return undefined;
};

/**
 * Display label for an attachment. Falls back to the URL's last path segment (a unique
 * identifier) when no name is available, so file links never show a generic placeholder.
 */
const getAttachmentDisplayName = (attachment: MessageAttachment): string | undefined => {
  if (attachment.name) return attachment.name;
  try {
    const segment = new URL(attachment.url).pathname.split("/").filter(Boolean).pop();
    return segment ? decodeURIComponent(segment) : undefined;
  } catch {
    return undefined;
  }
};

export interface MessageAttachment {
  /**
   * URL or secure file identifier for the attachment
   */
  url: string;
  /**
   * Type of the attachment (e.g., 'image', 'video', 'file')
   */
  type?: string;
  /**
   * Optional display name for the attachment
   */
  name?: string;
}

export interface MessageBubbleProps {
  /**
   * The message content to display
   */
  content: string;
  /**
   * The timestamp of the message
   */
  timestamp: Date;
  /**
   * Whether this is an outbound message (sent by the current user)
   */
  isOutbound: boolean;
  /**
   * Information about the sender
   */
  sender: {
    type: "user" | "enduser";
    name: string;
    avatar?: string;
    id: string;
  };
  /**
   * Optional array of image attachments to display
   */
  attachments?: MessageAttachment[];
  /**
   * Optional additional className for the container
   */
  className?: string;
  /**
   * Optional additional className for the message bubble
   */
  bubbleClassName?: string;
  status?: {
    type: "sending" | "error" | "scheduled";
    message?: string;
  };
  hideAvatar?: boolean;
  /** When true, highlights the bubble to indicate the current user is mentioned. */
  isHighlighted?: boolean;
}

/**
 * Renders a single media attachment (image, video, or audio) inline.
 *
 * When the attachment kind cannot be determined from its metadata (typeless, extensionless
 * URLs such as SMS `mediaURLs`), it is rendered optimistically as an image; if the image fails
 * to load it falls back to a clickable file link, and a detached `<video>` element probes the
 * URL to upgrade the link to an inline video/audio player when applicable. Cross-origin media
 * loads do not require CORS, so this works for opaque storage URLs.
 */
const MessageMediaAttachment = ({ attachment, isOutbound }: { attachment: MessageAttachment; isOutbound: boolean }) => {
  const declaredKind = getDeclaredKind(attachment);
  const [kind, setKind] = useState<AttachmentKind | undefined>(declaredKind);

  const handleImageError = () => {
    // Fall back to a clickable link immediately, then probe to upgrade to video/audio if possible.
    setKind("file");

    const probe = document.createElement("video");
    const cleanup = () => {
      probe.onloadedmetadata = null;
      probe.onerror = null;
      probe.removeAttribute("src");
    };
    probe.onloadedmetadata = () => {
      setKind(probe.videoWidth > 0 ? "video" : "audio");
      cleanup();
    };
    probe.onerror = cleanup;
    probe.preload = "metadata";
    probe.src = attachment.url;
  };

  const resolvedKind = kind ?? "image";

  if (resolvedKind === "video") {
    return (
      <video controls preload="metadata" className="rounded max-w-full max-h-64">
        <source src={attachment.url} type={attachment.type || undefined} />
      </video>
    );
  }
  if (resolvedKind === "audio") {
    return <audio controls src={attachment.url} className="w-full" />;
  }
  if (resolvedKind === "file") {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "flex items-center gap-2 p-2 rounded border transition-colors",
          isOutbound ? "border-primary-foreground/20 hover:bg-primary-foreground/10" : "border-border hover:bg-accent"
        )}
      >
        <File size={16} className="flex-shrink-0" />
        <span className="flex flex-col min-w-0">
          <span className="text-sm truncate">{getAttachmentDisplayName(attachment) || "File attachment"}</span>
          {attachment.type && <span className="text-xs opacity-70 truncate">{attachment.type}</span>}
        </span>
      </a>
    );
  }
  return (
    <img
      src={attachment.url}
      alt={attachment.name || "Image attachment"}
      className="rounded max-w-full max-h-64 object-contain"
      onError={declaredKind === "image" ? undefined : handleImageError}
    />
  );
};

/**
 * MessageBubble - A reusable message bubble component for chat/messaging interfaces
 *
 * Displays a message with sender avatar, content, and timestamp. Uses flex-row-reverse
 * for outbound messages to position the avatar on the right.
 *
 * @example
 * ```tsx
 * <MessageBubble
 *   content="Hello, how are you?"
 *   timestamp={new Date()}
 *   isOutbound={false}
 *   sender={{ name: "John Doe", avatar: "https://..." }}
 *   formatTimestamp={(date) => formatDateTime(date, "MM/DD/YY at hh:mm a")}
 *   formatSenderName={(sender) => formatName(sender)}
 * />
 * ```
 */
export const MessageBubble = ({
  content,
  timestamp,
  isOutbound,
  sender,
  attachments,
  className,
  bubbleClassName,
  status,
  hideAvatar,
  isHighlighted,
}: MessageBubbleProps) => {
  const hasAttachments = !!attachments && attachments.length > 0;

  return (
    <div
      className={cn("flex gap-3 my-2 w-full", isOutbound ? "content-end flex-row-reverse" : "content-start", className)}
    >
      {!hideAvatar && (
        <div className="h-full flex-col items-start">
          <Avatar className="w-8 h-8">
            <AvatarImage src={sender.avatar} alt={sender.name} />
            <AvatarFallback useStaticBackground={true} id={sender.id}>
              {formatInitials(sender.name)}
            </AvatarFallback>
          </Avatar>
        </div>
      )}
      <div className={cn("flex flex-col max-w-[70%]", isOutbound && "items-end")}>
        <div className={cn("flex items-center gap-2 mb-1 text-xs", isOutbound && "flex-row-reverse")}>
          {!!sender.name && <span className="font-semibold">{sender.name}</span>}
          <span className="text-muted-foreground opacity-70 flex items-center gap-1">
            {status?.type === "scheduled" && <Clock size={12} />}
            {formatDateTime(timestamp, "MM/DD/YYYY at hh:mm a", { unit: "days", amount: 7, direction: "both" })}
          </span>
        </div>
        {(content || hasAttachments) && (
          <div
            className={cn(
              "rounded-lg w-full max-w-fit p-3",
              isOutbound ? "bg-primary text-primary-foreground" : "bg-muted",
              isHighlighted && !isOutbound && "bg-blue-200 dark:bg-blue-800/60 ring-1 ring-blue-300 dark:ring-blue-700",
              status?.type === "sending" && isOutbound && "bg-primary/60",
              status?.type === "error" && isOutbound && "bg-white text-destructive border-[1.5px] border-destructive",
              bubbleClassName
            )}
          >
            {hasAttachments && (
              <div className={cn("flex flex-col gap-2", content && "mb-2")}>
                {attachments.map((attachment, index) => (
                  <MessageMediaAttachment
                    key={`${attachment.url}-${index}`}
                    attachment={attachment}
                    isOutbound={isOutbound}
                  />
                ))}
              </div>
            )}
            {content && (
              <div className="text-sm text-left">
                <p className="whitespace-pre-wrap break-words">{content}</p>
              </div>
            )}
          </div>
        )}
        {status?.message && <div className="text-xs mt-1 opacity-70">{status.message}</div>}
      </div>
    </div>
  );
};
